import { Modal } from '@/components';
import clsx from 'clsx';
import { isWebView, sendMessageToWebView } from '@/utility/webview';
import { useSolveQuestion } from '@/context/solve-question-context';
import { SOLVE_TYPE } from '@/constants';
import { useSizes } from '@/utility/responsive.ts';
import { Fragment, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useQueryProvider } from '@/hooks';
import { useApi } from '@/api/useApi';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useAuthStore } from '@/zustand/auth/store.ts';
import Clock from '@/assets/images/clock.png';
import { PostMessage, SolveType } from '@/constants/post-message.ts';

const isMobile = isWebView();

export const SolveBottomSheet = ({ setUploadedVideoData, setIsVideoUploadModalOpen }) => {
	const { data: authData } = useAuthStore();
	const fileInputRef = useRef(null);
	const { SolverQuestionControllerApi } = useApi();

	const {
		isOpenSolveBottomSheet,
		setIsOpenSolveBottomSheet,
		selectedSolveType,
		setSelectedSolveType,
		setIsOpenImageCropModal,
		getActiveQuestion,
		setIsOpenDirectSendQuestion,
		activeTimer,
		isResolveMode,
		resolveQueueId
	} = useSolveQuestion();

	const { data: solverPassQuestion } = useQueryProvider([QUERY_KEYS.solverPassQuestion], () => SolverQuestionControllerApi.getPassReasons(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: true
	});

	const activeQuestion = useMemo(() => getActiveQuestion(), [getActiveQuestion]);

	const onCloseSolveBottomSheet = () => {
		setIsOpenSolveBottomSheet(false);
		setSelectedSolveType(null);
	};

	const handleFileSelect = event => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith('video/')) {
			toast.error('Lütfen geçerli bir video dosyası seçin.');
			return;
		}

		const isMP4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');

		if (!isMP4) {
			toast.error('Sadece MP4 formatında video yükleyebilirsiniz.');
			return;
		}

		const videoUrl = URL.createObjectURL(file);
		setUploadedVideoData({
			blob: file,
			url: videoUrl,
			filename: file.name,
			fromCamera: false
		});

		setIsVideoUploadModalOpen(true);
		onCloseSolveBottomSheet();

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const onNavigateSolve = () => {
		const timerInfo = activeTimer?.getTimerInfo();

		if (selectedSolveType === SOLVE_TYPE.WHITE_BOARD) {
			if (isWebView()) {
				const messageData = {
					type: PostMessage.NAVIGATE_SOLVE,
					solveType: SolveType.WHITE_BOARD,
					questionImage: activeQuestion?.questionImage,
					accessToken: authData?.access_token || null,
					isResolveMode,
					resolveQueueId,
					questionInfo: {
						grade: activeQuestion?.curriculumName,
						subject: activeQuestion?.subject,
						topic: activeQuestion?.curriculumElementUnitName
					},
					activeQuestion: {
						...activeQuestion,
						remainingTime: timerInfo?.remainingTime || 0,
						isTimerActive: activeTimer?.isActive || false,
						timerCreatedAt: timerInfo?.createdAt,
						timerTimeoutAt: timerInfo?.timeoutAt
					},
					timestamp: Date.now()
				};

				sendMessageToWebView(messageData);
			} else {
				setIsOpenImageCropModal(true);
			}
		} else if (selectedSolveType === SOLVE_TYPE.CAMERA) {
			if (isWebView()) {
				const messageData = {
					type: PostMessage.NAVIGATE_SOLVE,
					solveType: SolveType.CAMERA,
					questionImage: activeQuestion?.questionImage,
					accessToken: authData?.access_token || null,
					isResolveMode,
					resolveQueueId,
					questionInfo: {
						grade: activeQuestion?.curriculumName,
						subject: activeQuestion?.subject,
						topic: activeQuestion?.curriculumElementUnitName
					},
					solverPassQuestion: solverPassQuestion,
					remainingTime: timerInfo?.remainingTime || 0,
					isTimerActive: activeTimer?.isActive || false,
					timerCreatedAt: timerInfo?.createdAt,
					timerTimeoutAt: timerInfo?.timeoutAt,
					interactionId: activeQuestion?.interactionId,
					questionId: activeQuestion?.questionId,
					isResolveMode,
					resolveQueueId,
					activeQuestion: {
						...activeQuestion,
						remainingTime: timerInfo?.remainingTime || 0,
						isTimerActive: activeTimer?.isActive || false,
						timerCreatedAt: timerInfo?.createdAt,
						timerTimeoutAt: timerInfo?.timeoutAt
					},
					timestamp: Date.now()
				};

				sendMessageToWebView(messageData);
			} else {
				if (!isMobile && fileInputRef.current) {
					fileInputRef.current.click();
					return;
				}
				setIsOpenDirectSendQuestion(true);
			}
		}
		onCloseSolveBottomSheet();
	};

	return (
		<Fragment>
			<input
				ref={fileInputRef}
				type="file"
				accept="video/mp4,.mp4"
				style={{ display: 'none' }}
				onChange={handleFileSelect}
			/>

			<Modal
				isOpen={isOpenSolveBottomSheet}
				onClose={onCloseSolveBottomSheet}
				contentClassName="solver-question-solve-bottom-sheet"
				showCloseButton
				closeOnClickOutside={isMobile}
				variant={isMobile ? 'bottom' : 'centered'}
				customXIcon={<i className="icons-x" />}>
				<h6 className="bottom-sheet-title">Soru çözümünde hangi anlatım yöntemini kullanmak istersiniz?</h6>

				<div className="option-wrapper">
					<button
						onClick={() => setSelectedSolveType(SOLVE_TYPE.WHITE_BOARD)}
						className={clsx('option-button', {
							active: selectedSolveType === SOLVE_TYPE.WHITE_BOARD
						})}>
						<i className="icons-whiteboard" />
						<p className="option-text">
							<b>Beyaz tahta</b> {isMobile && <br />} kullanmak <br /> istiyorum
						</p>
					</button>
					<button
						onClick={() => setSelectedSolveType(SOLVE_TYPE.CAMERA)}
						className={clsx('option-button', {
							active: selectedSolveType === SOLVE_TYPE.CAMERA
						})}>
						<i className="icons-camera" />
						<p className="option-text">
							{isMobile ? (
								<Fragment>
									<b>Kamerayla</b> <br /> devam etmek <br /> istiyorum
								</Fragment>
							) : (
								<Fragment>
									<b>
										Bilgisayardan çözüm <br /> videosu{' '}
									</b>{' '}
									yüklemek <br />
									istiyorum
								</Fragment>
							)}
						</p>
					</button>
				</div>

				<div className="info-wrapper">
					<h6 className="info-wrapper-title">Önemli Bilgiler</h6>

					<div className="info-list">
						<div className="info-list-item">
							<div className="info-list-item-icon-wrapper">
								<i className="icons-danger-outline" />
							</div>
							<p>Anlatımınızı öğrencinin seviyesine uygun şekilde yapmanız önemlidir.</p>
						</div>
						<div className="info-list-item">
							<div className="info-list-item-icon-wrapper">
								<img src={Clock} />
							</div>
							<p>
								<b>20 dakika</b> içinde çözüm yapmazsanız, soru havuzdan çıkar ve hesabınız askıya alınabilir.
							</p>
						</div>
						<div className="info-list-item">
							<div className="info-list-item-icon-wrapper">
								<i className="icons-crop" />
							</div>
							<p>Soru fotoğrafını net, düz ve sadece soruyu içerecek şekilde kırpın.</p>
						</div>
					</div>
				</div>

				<button
					onClick={onNavigateSolve}
					disabled={!selectedSolveType}
					className="start-solve-button">
					Anladım, Soru Çözümüne Başla
				</button>
			</Modal>
		</Fragment>
	);
};
